/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {Segment} from '../models/segment.model';
import {ConfigService} from './config.service';

@Injectable({
  providedIn: 'root'
})
export class SegmentService {

  // Constant to hold the endpoint value
  private readonly segmentEndpoint: string;

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
    this.segmentEndpoint = `${this.configService.baseApiUrl}segment`;
  }

  /**
   * Calls backend to retrieve segments associated with GM Global Scheme.
   */
  getMappedSegments(): Observable<Segment[]> {
    return this.http.get<any>(this.segmentEndpoint + '/allMappedSegments')
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Calls backend to retrieve segments not associated with GM Global Scheme.
   */
  getNonMappedSegments(): Observable<Segment[]> {
    return this.http.get<any>(this.segmentEndpoint + '/allNonMappedSegments')
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Calls backend to create segments from user input.
   * @param segment The segment object passed in.
   */
  createSegment(segment): Observable<Segment> {
    return this.http.post<any>(this.segmentEndpoint, segment)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Deletes the selected level one segment, as well as all children associated to it.
   * @param segment The selected segment from UI.
   */
  deleteSegment(segment: Segment): Observable<Segment> {
    return this.http.request<any>('DELETE', this.segmentEndpoint, {
      body: segment
    }).pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   * @return void
   */
  handleError(errorResponse: HttpErrorResponse): any {
    let errorMessage = 'An unexpected error occurred.';
    switch (errorResponse.status) {
      case HttpStatusCode.UnprocessableEntity:
      case HttpStatusCode.BadRequest: {
        if (errorResponse.error && errorResponse.error.message) {
          errorMessage = errorResponse.error.message;
        }
        break;
      }
    }
    return throwError(errorMessage);
  }
}
